import React, { useState, useEffect } from 'react';
import { renderColoredText } from '../utils/colorParser';
import './MyBoosts.css';

function MyBoosts({ authToken, onTokenExpiration }) {
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBoostData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://api.vmp.ir/dev/api.php?work=getData', {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.boosts) {
        setBoosts(data.boosts);
      }
    } catch (error) {
      console.error('Failed to fetch boost data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchBoostData();
    }
  }, [authToken]);

  const getBoostIcon = () => {
    return '⚡';
  };

  const getStatusColor = (start, expire) => {
    const now = Math.floor(Date.now() / 1000);
    if (now < start) {
      return '#f59e0b'; // pending
    } else if (now > expire) {
      return '#ef4444'; // expired
    } else {
      return '#10b981'; // active
    }
  };

  const getStatus = (start, expire) => {
    const now = Math.floor(Date.now() / 1000);
    if (now < start) {
      return 'pending';
    } else if (now > expire) {
      return 'expired';
    } else {
      return 'active';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpiringSoon = (expire) => {
    const expireDate = new Date(expire * 1000);
    const today = new Date();
    const diffTime = expireDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const getRemainingDays = (expire) => {
    const expireDate = new Date(expire * 1000);
    const today = new Date();
    const diffTime = expireDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Expires today';
    } else if (diffDays === 1) {
      return '1 day';
    } else {
      return `${diffDays} days`;
    }
  };

  if (loading) {
    return (
      <div className="my-boosts">
        <div className="boosts-header">
          <h3>My Boosts</h3>
          <p>Loading your boosts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-boosts">
        <div className="boosts-header">
          <h3>My Boosts</h3>
          <p>Error loading boosts: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-boosts">
      <div className="boosts-header">
        <h3>My Boosts</h3>
        <p>Manage your active boosts and subscriptions</p>
      </div>
      
      <div className="boosts-grid">
        {boosts.map((boost) => {
          const status = getStatus(boost.start, boost.expire);
          return (
            <div 
              key={boost.id} 
              className={`boost-card ${status} ${isExpiringSoon(boost.expire) ? 'expiring-soon' : ''}`}
            >
              <div className="boost-header">
                <div className="boost-title">
                  <div className="boost-icon">
                    {getBoostIcon()}
                  </div>
                  <div className="boost-info">
                    <h4>{boost.data?.name ? renderColoredText(boost.data.name) : 'Unknown Server'}</h4>
                    <span className="boost-server">Server ID: {boost.server_id}</span>
                  </div>
                </div>
                <div className="boost-status">
                  <span 
                    className="status-indicator"
                    style={{ backgroundColor: getStatusColor(boost.start, boost.expire) }}
                  ></span>
                  <span className="status-text">{status}</span>
                </div>
              </div>
              
              <div className="boost-details">
                <div className="boost-field">
                  <span className="field-label">Server ID:</span>
                  <span className="field-value">{boost.data?.id || 'N/A'}</span>
                </div>
                <div className="boost-field">
                  <span className="field-label">Owner:</span>
                  <span className="field-value">{boost.data?.ownerName || 'Unknown'}</span>
                </div>
                <div className="boost-field">
                  <span className="field-label">Started:</span>
                  <span className="field-value">{formatTimestamp(boost.start)}</span>
                </div>
                <div className="boost-field">
                  <span className="field-label">Days Remaining:</span>
                  <span className={`field-value ${status === 'expired' ? 'expired-text' : isExpiringSoon(boost.expire) ? 'warning-text' : 'active-text'}`}>
                    {getRemainingDays(boost.expire)}
                  </span>
                </div>
              </div>
              
              <div className="boost-footer">
                <div className="boost-expiry">
                  <span className="expiry-label">
                    {status === 'expired' ? 'Expired:' : 'Expires:'}
                  </span>
                  <span className="expiry-date">
                    {formatTimestamp(boost.expire)}
                  </span>
                  {isExpiringSoon(boost.expire) && (
                    <span className="expiry-warning">⚠ Expiring Soon</span>
                  )}
                </div>
                <div className="boost-actions">
                  {status === 'active' ? (
                    <button className="manage-button">Manage</button>
                  ) : (
                    <button className="renew-button">Renew</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        <div className="browse-boosts-card">
          <div className="browse-content">
            <div className="browse-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h4>Browse Boosts</h4>
            <p>Discover new ways to enhance your servers</p>
            <button 
              className="browse-button"
              onClick={() => window.open('https://list.vmp.ir', '_blank')}
            >
              View Store
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyBoosts;